import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../api';
import { useAdmin } from './AdminContext';
import { pageState } from './AdminLayout';
import Markdown from '../markdown/Markdown';
import Modal, { Field } from './Modal';
import { ArrowRight, ArrowUpRight, History, Settings, Trash } from '../components/Icons';

/* ── Toolbar snippets ─────────────────────────────────────────────────────── */

const SNIPPETS = [
  { label: 'H2', title: 'Section heading', block: '## Heading' },
  { label: 'H3', title: 'Sub-heading', block: '### Heading' },
  { label: 'B', title: 'Bold', wrap: ['**', '**'], className: 'is-bold' },
  { label: '`code`', title: 'Inline code', wrap: ['`', '`'], className: 'is-mono' },
  { label: 'Link', title: 'Link (use a page slug for internal links)', wrap: ['[', '](page-slug)'] },
  { sep: true },
  { label: 'Code', title: 'Code block', block: '```json title="Response"\n{\n  "ok": true\n}\n```' },
  { label: 'Table', title: 'Table', block: '| Field | Description |\n| --- | --- |\n| `name` | What it does |' },
  { label: 'Callout', title: 'Callout (info, warn, tip, danger)', block: ':::callout{type=info}\nSomething worth knowing.\n:::' },
  { label: 'Endpoint', title: 'API endpoint', block: '::endpoint{method=POST path="https://api-prod.veritage.io/api/v1/…" auth="X-Api-Key: pk_live_..."}' },
  { label: 'Steps', title: 'Numbered steps', block: ':::steps\n### First step\nWhat to do.\n\n### Second step\nWhat to do next.\n:::' },
  { label: 'Cards', title: 'Link cards', block: ':::cards\n- [Create a session](create-session) — Your first API call.\n- [API reference](endpoints) — Every endpoint and field.\n:::' },
  { label: 'Badge', title: 'Inline badge', inline: ':badge[Required]' },
  { label: 'Image', title: 'Image', block: '![Describe the image](https://…/image.png)' },
];

function applySnippet(textarea, value, snippet) {
  const { selectionStart: s, selectionEnd: e } = textarea;
  const selected = value.slice(s, e);
  let insert, cursorStart, cursorEnd;
  if (snippet.wrap) {
    const [a, b] = snippet.wrap;
    insert = `${a}${selected || 'text'}${b}`;
    cursorStart = s + a.length;
    cursorEnd = cursorStart + (selected || 'text').length;
  } else if (snippet.inline) {
    insert = snippet.inline;
    cursorStart = cursorEnd = s + insert.length;
  } else {
    const before = value.slice(0, s);
    const pad = before.length === 0 || before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n';
    insert = `${pad}${snippet.block}\n`;
    cursorStart = s + pad.length;
    cursorEnd = cursorStart + snippet.block.length;
  }
  return { next: value.slice(0, s) + insert + value.slice(e), cursorStart, cursorEnd };
}

/* ── Revisions ────────────────────────────────────────────────────────────── */

function RevisionsModal({ page, onClose, onRestored }) {
  const [revs, setRevs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.revisions(page.id).then((r) => { setRevs(r); setSelected(r[0] || null); });
  }, [page.id]);

  const restore = async () => {
    setBusy(true);
    const updated = await adminApi.restore(page.id, selected.id);
    onRestored(updated);
  };

  return (
    <Modal
      wide
      title="Publish history"
      onClose={onClose}
      footer={selected && <>
        <span className="cms-muted" style={{ marginRight: 'auto' }}>Restoring loads this version into your draft. Publish to make it live.</span>
        <button type="button" className="vt-btn vt-btn--sm" onClick={restore} disabled={busy}>Restore to draft</button>
      </>}
    >
      {!revs && <div className="docs-spinner" />}
      {revs && revs.length === 0 && <p className="cms-muted">This page hasn't been published yet.</p>}
      {revs && revs.length > 0 && (
        <div className="cms-revisions">
          <div className="cms-revisions-list">
            {revs.map((r, i) => (
              <button type="button" key={r.id} className={`cms-rev${selected?.id === r.id ? ' is-active' : ''}`} onClick={() => setSelected(r)}>
                <span className="cms-rev-date">{new Date(r.createdAt).toLocaleString()}</span>
                <span className="cms-rev-meta">{i === 0 ? 'Latest · ' : ''}{r.createdBy || 'unknown'}</span>
                {r.note && <span className="cms-rev-note">{r.note}</span>}
              </button>
            ))}
          </div>
          <div className="cms-revisions-preview">
            {selected && (
              <>
                <h1 className="docs-title" style={{ fontSize: 32 }}>{selected.title}</h1>
                <Markdown content={selected.content} />
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ── Editor ───────────────────────────────────────────────────────────────── */

export default function Editor() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { tree, refreshTree } = useAdmin();
  const [page, setPage] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saveState, setSaveState] = useState('saved'); // saved | dirty | saving | error
  const [error, setError] = useState(null);
  const [view, setView] = useState(() => (window.innerWidth > 1180 ? 'split' : 'write'));
  const [panel, setPanel] = useState(null); // settings | history | publish | delete
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef(null);
  const previewRef = useRef(null);
  const lastSaved = useRef(null);

  // Load page
  useEffect(() => {
    setPage(null);
    setDraft(null);
    setError(null);
    adminApi.page(pageId).then((p) => {
      setPage(p);
      const d = { title: p.title, description: p.description || '', content: p.content, slug: p.slug, section: p.section };
      setDraft(d);
      lastSaved.current = d;
      setSaveState('saved');
    }).catch((e) => setError(e.message));
  }, [pageId]);

  const save = useCallback(async () => {
    if (!draft || !lastSaved.current) return page;
    const changed = Object.keys(draft).filter((k) => draft[k] !== lastSaved.current[k]);
    if (!changed.length) return page;
    const body = Object.fromEntries(changed.map((k) => [k, draft[k]]));
    const snapshot = draft;
    setSaveState('saving');
    try {
      const updated = await adminApi.savePage(pageId, body);
      lastSaved.current = snapshot;
      setPage(updated);
      setSaveState((s) => (s === 'saving' ? 'saved' : s));
      setError(null);
      if (changed.some((k) => ['title', 'slug', 'section'].includes(k)) || !page?.hasUnpublishedChanges) refreshTree();
      return updated;
    } catch (e) {
      setSaveState('error');
      setError(e.message);
      throw e;
    }
  }, [draft, page, pageId, refreshTree]);

  // Autosave drafts after a short pause in typing (drafts are never public).
  useEffect(() => {
    if (saveState !== 'dirty') return undefined;
    const t = setTimeout(() => { save().catch(() => {}); }, 1200);
    return () => clearTimeout(t);
  }, [draft, saveState, save]);

  // ⌘S / Ctrl+S
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') { e.preventDefault(); save().catch(() => {}); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save]);

  // Warn before leaving with unsaved edits
  useEffect(() => {
    const onBeforeUnload = (e) => { if (saveState === 'dirty' || saveState === 'saving') { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [saveState]);

  const update = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setSaveState('dirty');
  };

  const insert = (snippet) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const { next, cursorStart, cursorEnd } = applySnippet(ta, draft.content, snippet);
    update({ content: next });
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(cursorStart, cursorEnd); });
  };

  const onEditorKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      insert({ inline: '  ' });
    }
  };

  // Keep the preview roughly in step with the editor scroll position.
  const syncScroll = (e) => {
    const el = e.target;
    const preview = previewRef.current;
    if (!preview || view !== 'split') return;
    const ratio = el.scrollTop / Math.max(1, el.scrollHeight - el.clientHeight);
    preview.scrollTop = ratio * (preview.scrollHeight - preview.clientHeight);
  };

  const publish = async () => {
    setBusy(true);
    try {
      await save();
      const updated = await adminApi.publish(pageId, note);
      setPage(updated);
      setNote('');
      setPanel(null);
      await refreshTree();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const runAction = async (fn, after) => {
    setBusy(true);
    try {
      const updated = await fn();
      if (updated) {
        setPage(updated);
        const d = { title: updated.title, description: updated.description || '', content: updated.content, slug: updated.slug, section: updated.section };
        setDraft(d);
        lastSaved.current = d;
        setSaveState('saved');
      }
      await refreshTree();
      after?.();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const location = useMemo(() => {
    if (!tree || !page) return null;
    const product = tree.find((p) => p.id === page.productId);
    const version = product?.versions.find((v) => v.id === page.versionKey);
    return { product, version };
  }, [tree, page]);

  const sections = useMemo(() => [...new Set(location?.version?.pages.map((p) => p.section) || [])], [location]);

  if (error && !page) return <div className="cms-pad"><div className="cms-alert">{error}</div></div>;
  if (!page || !draft) return <div className="cms-pad"><div className="docs-spinner" /></div>;

  const st = pageState({ ...page, hasUnpublishedChanges: page.hasUnpublishedChanges || saveState === 'dirty' });
  const basePath = `/${page.productId}/${page.versionKey}`;
  const saveLabel = { saved: 'Draft saved', dirty: 'Unsaved changes', saving: 'Saving…', error: 'Save failed' }[saveState];
  const canPublish = st.key !== 'live';

  return (
    <div className="cms-editor">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="cms-editor-head">
        <div className="cms-editor-crumbs">
          <span className="vt-eyebrow">{location?.product?.label} · {page.versionKey} · {draft.section}</span>
          <span className={`cms-status cms-status--${st.key}`}><i className={`cms-dot cms-dot--${st.key}`} />{st.label}</span>
          <span className={`cms-save cms-save--${saveState}`}>{saveLabel}</span>
        </div>
        <div className="cms-editor-actions">
          <div className="cms-segment" role="tablist" aria-label="Editor view">
            {['write', 'split', 'preview'].map((v) => (
              <button type="button" key={v} role="tab" aria-selected={view === v} className={view === v ? 'is-active' : ''} onClick={() => setView(v)}>
                {v[0].toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <button type="button" className="docs-icon-btn" title="Page settings" aria-label="Page settings" onClick={() => setPanel('settings')}><Settings size={18} /></button>
          <button type="button" className="docs-icon-btn" title="Publish history" aria-label="Publish history" onClick={() => setPanel('history')}><History size={18} /></button>
          {page.published && (
            <a className="docs-icon-btn" href={`${basePath}/${page.slug}`} target="_blank" rel="noreferrer" title="View live page" aria-label="View live page"><ArrowUpRight size={18} /></a>
          )}
          <button type="button" className="vt-btn vt-btn--sm" disabled={!canPublish || busy} onClick={() => setPanel('publish')}>
            {page.published ? 'Publish changes' : 'Publish'}
            <span className="vt-btn-arrow"><ArrowRight size={12} /></span>
          </button>
        </div>
      </div>

      {error && <div className="cms-alert cms-alert--inline">{error}</div>}

      {/* ── Panes ────────────────────────────────────────────────────────── */}
      <div className={`cms-panes is-${view}`}>
        {view !== 'preview' && (
          <div className="cms-pane cms-pane--write">
            <input
              className="cms-title-input"
              value={draft.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Page title"
              aria-label="Page title"
            />
            <textarea
              className="cms-desc-input"
              value={draft.description}
              onChange={(e) => update({ description: e.target.value })}
              placeholder="Lead paragraph shown under the title (optional)"
              aria-label="Lead paragraph"
              rows={2}
            />
            <div className="cms-toolbar" role="toolbar" aria-label="Insert">
              {SNIPPETS.map((s, i) => s.sep
                ? <span key={i} className="cms-toolbar-sep" />
                : <button type="button" key={s.label} title={s.title} className={s.className} onClick={() => insert(s)}>{s.label}</button>)}
            </div>
            <textarea
              ref={textareaRef}
              className="cms-md-input"
              value={draft.content}
              onChange={(e) => update({ content: e.target.value })}
              onKeyDown={onEditorKeyDown}
              onScroll={syncScroll}
              spellCheck
              aria-label="Page content (markdown)"
            />
          </div>
        )}
        {view !== 'write' && (
          <div className="cms-pane cms-pane--preview" ref={previewRef}>
            <div className="cms-preview-badge vt-eyebrow">Preview</div>
            <div className="cms-preview-page">
              <div className="vt-eyebrow docs-header-eyebrow">{draft.section}</div>
              <h1 className="docs-title">{draft.title || 'Untitled'}</h1>
              {draft.description && <div className="docs-lead"><Markdown content={draft.description} basePath={basePath} /></div>}
              <Markdown content={draft.content} basePath={basePath} />
            </div>
          </div>
        )}
      </div>

      {/* ── Publish ──────────────────────────────────────────────────────── */}
      {panel === 'publish' && (
        <Modal
          title={page.published ? 'Publish changes' : 'Publish page'}
          onClose={() => setPanel(null)}
          footer={<>
            <button type="button" className="vt-btn vt-btn--soft vt-btn--sm" onClick={() => setPanel(null)}>Cancel</button>
            <button type="button" className="vt-btn vt-btn--sm" onClick={publish} disabled={busy}>
              {busy ? 'Publishing…' : 'Publish now'} <span className="vt-btn-arrow"><ArrowRight size={12} /></span>
            </button>
          </>}
        >
          <p className="cms-muted" style={{ marginTop: 0 }}>
            <strong>{draft.title}</strong> will go live at <code className="docs-inline-code">{basePath}/{draft.slug}</code> within a minute.
          </p>
          <Field label="What changed? (optional)" hint="Shown in the publish history.">
            <input className="cms-input" autoFocus value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && publish()} placeholder="e.g. Added webhook retry policy" />
          </Field>
        </Modal>
      )}

      {/* ── Settings ─────────────────────────────────────────────────────── */}
      {panel === 'settings' && (
        <Modal title="Page settings" onClose={() => setPanel(null)} footer={<button type="button" className="vt-btn vt-btn--sm" onClick={() => setPanel(null)}>Done</button>}>
          <div className="cms-form">
            <Field label="URL slug" hint={`Live at ${basePath}/${draft.slug || '…'}. Changing it breaks existing links.`}>
              <input className="cms-input" value={draft.slug} onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} />
            </Field>
            <Field label="Sidebar section" hint="Pages with the same section are grouped in the sidebar.">
              <input className="cms-input" list="cms-edit-sections" value={draft.section} onChange={(e) => update({ section: e.target.value })} />
              <datalist id="cms-edit-sections">{sections.map((s) => <option key={s} value={s} />)}</datalist>
            </Field>
            <div className="cms-danger">
              <div className="vt-eyebrow">Danger zone</div>
              {page.published && page.hasUnpublishedChanges && (
                <div className="cms-danger-row">
                  <span>Discard all draft changes and go back to the live version.</span>
                  <button type="button" className="vt-btn vt-btn--soft vt-btn--sm" disabled={busy} onClick={() => runAction(() => adminApi.discard(page.id), () => setPanel(null))}>Discard changes</button>
                </div>
              )}
              {page.published && (
                <div className="cms-danger-row">
                  <span>Take the page off the public site. The draft is kept.</span>
                  <button type="button" className="vt-btn vt-btn--soft vt-btn--sm" disabled={busy} onClick={() => runAction(() => adminApi.unpublish(page.id), () => setPanel(null))}>Unpublish</button>
                </div>
              )}
              <div className="cms-danger-row">
                <span>Delete this page and its history permanently.</span>
                <button type="button" className="vt-btn vt-btn--soft vt-btn--sm cms-danger-btn" onClick={() => setPanel('delete')}><Trash size={14} /> Delete</button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {panel === 'delete' && (
        <Modal
          title="Delete page?"
          onClose={() => setPanel(null)}
          footer={<>
            <button type="button" className="vt-btn vt-btn--soft vt-btn--sm" onClick={() => setPanel(null)}>Cancel</button>
            <button type="button" className="vt-btn vt-btn--sm cms-btn-danger" disabled={busy} onClick={() => runAction(async () => { await adminApi.deletePage(page.id); return null; }, () => navigate('/admin'))}>Delete permanently</button>
          </>}
        >
          <p style={{ margin: 0 }}><strong>{page.title}</strong> and all of its publish history will be removed. This can't be undone.</p>
        </Modal>
      )}

      {panel === 'history' && (
        <RevisionsModal
          page={page}
          onClose={() => setPanel(null)}
          onRestored={(updated) => runAction(async () => updated, () => setPanel(null))}
        />
      )}
    </div>
  );
}
